import {
  CheckCircle,
  IconProps,
  Info,
  SignOut,
  Spinner,
  Users,
  WarningCircle,
} from '@phosphor-icons/react';
import { Card } from 'src/components/Card';
import { format } from 'date-fns';
import { Tooltip } from 'src/components/Tooltip';

interface Metric {
  value: number;
  label: string;
  icon: React.ComponentType<IconProps>;
  iconColor: string;
}

interface CampaignMetricsProps {
  totalContacts: number;
  queued: number;
  active: number;
  errored: number;
  exited: number;
  startDate: Date;
  className?: string;
}

export const CampaignMetrics = ({
  totalContacts,
  queued,
  active,
  errored,
  exited,
  startDate,
  className = '',
}: CampaignMetricsProps) => {
  const metrics: Metric[] = [
    {
      value: queued,
      label: 'Queued',
      icon: Spinner,
      iconColor: 'text-warning',
    },
    {
      value: active,
      label: 'Active',
      icon: CheckCircle,
      iconColor: 'text-success',
    },
    {
      value: errored,
      label: 'Errored',
      icon: WarningCircle,
      iconColor: 'text-error',
    },
    {
      value: exited,
      label: 'Exited',
      icon: SignOut,
      iconColor: 'text-neutral-600',
    },
  ];

  const parsedStartDate =
    startDate && !isNaN(startDate.getTime())
      ? format(startDate, 'MMM. dd, yyyy')
      : '-';

  const startDateIsFuture = startDate && startDate > new Date();
  const myTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-10 gap-6 ${className}`}>
      <div className="lg:col-span-8">
        <Card className="h-full p-6">
          <div className="flex items-center h-full gap-16">
            {/* Total contacts */}
            <div className="text-left flex flex-col justify-center">
              <div className="text-2xl font-medium text-neutral-900 mb-2">
                {totalContacts}
              </div>
              <div className="flex items-center gap-2">
                <Users size={20} className="text-primary" />
                <span className="text-sm text-neutral-600">Total contacts</span>
              </div>
            </div>

            {/* Vertical border separator */}
            <div className="flex items-center justify-center">
              <div className="w-px h-12 bg-neutral-100"></div>
            </div>

            {/* Rest of metrics grouped together */}
            <div className="flex items-center justify-between flex-1">
              {metrics.map((metric, index) => (
                <div
                  key={index}
                  className="text-left flex flex-col justify-center"
                >
                  <div className="text-2xl font-medium text-neutral-900 mb-2">
                    {metric.value}
                  </div>
                  <div className="flex items-center gap-2">
                    <metric.icon size={20} className={metric.iconColor} />
                    <span className="text-sm text-neutral-600">
                      {metric.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* Start Date Card - 30% */}
      <div className="lg:col-span-2">
        <Card className="h-full p-6">
          <div className="text-left flex flex-col justify-center h-full">
            <div className="text-2xl font-medium text-neutral-900">
              {parsedStartDate}
              <Tooltip content={myTimeZone}>
                <Info className="ml-2 w-4 h-4 text-neutral-600" />
              </Tooltip>
            </div>
            <div className="text-sm text-neutral-600">
              {startDateIsFuture ? 'Scheduled for' : 'Started on'}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
